import { useState } from 'react';
import { Check, Plus } from 'lucide-react';

import ActionButton from '../../../components/Buttons/ActionButton';
import InputField from '../../../components/Forms/Input/InputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import {
  RestaurantPrinterConnectionType,
  RestaurantPrinterDTO,
  RestaurantPrinterForm,
  RestaurantPrinterType,
} from '../../../types/restaurant/RestaurantPrinterTypes';

interface Props {
  printers: RestaurantPrinterDTO[];
  value: number | '';
  disabled?: boolean;
  onChange: (printerId: number | '') => void;
  onCreatePrinter: (
    input: Partial<RestaurantPrinterForm> & { name: string }
  ) => Promise<RestaurantPrinterDTO | null>;
}

const TYPE_OPTIONS: { value: RestaurantPrinterType; label: string }[] = [
  { value: 'KITCHEN', label: 'Comandas de cocina' },
  { value: 'RECEIPT', label: 'Tickets / precuenta' },
  { value: 'LABEL', label: 'Etiquetas' },
];

const CONNECTION_OPTIONS: {
  value: RestaurantPrinterConnectionType;
  label: string;
}[] = [
  { value: 'NETWORK', label: 'Red (IP)' },
  { value: 'LOCAL', label: 'Local / USB' },
];

/**
 * Selector de impresora de comandas con alta "＋ Nueva impresora" en línea
 * (mini-formulario). El código/estado los completa el backend.
 */
const PrinterQuickField = ({
  printers,
  value,
  disabled,
  onChange,
  onCreatePrinter,
}: Props) => {
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [printerName, setPrinterName] = useState('');
  const [printerType, setPrinterType] =
    useState<RestaurantPrinterType>('KITCHEN');
  const [connectionType, setConnectionType] =
    useState<RestaurantPrinterConnectionType>('NETWORK');
  const [ipAddress, setIpAddress] = useState('');
  const [port, setPort] = useState<number | ''>(9100);

  const isNetwork = connectionType === 'NETWORK';
  const canSave =
    !!name.trim() &&
    !!printerName.trim() &&
    (!isNetwork || !!ipAddress.trim()) &&
    !creating &&
    !disabled;

  const reset = () => {
    setName('');
    setPrinterName('');
    setPrinterType('KITCHEN');
    setConnectionType('NETWORK');
    setIpAddress('');
    setPort(9100);
  };

  const addPrinter = async () => {
    if (!canSave) return;
    setCreating(true);
    try {
      const created = await onCreatePrinter({
        name: name.trim(),
        printerName: printerName.trim(),
        printerType,
        connectionType,
        ipAddress: isNetwork ? ipAddress.trim() : '',
        port: isNetwork ? port : '',
      });
      if (created) {
        onChange(created.id);
        setShowNew(false);
        reset();
      }
    } finally {
      setCreating(false);
    }
  };

  if (showNew) {
    return (
      <div className="space-y-3 rounded-lg border border-primary/20 bg-primary/5 p-3 dark:border-primary/25 dark:bg-primary/10 md:col-span-2">
        <p className="text-sm font-semibold text-black dark:text-white">
          Nueva impresora
        </p>

        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
          <InputField
            label="Nombre"
            name="printerNewName"
            type="text"
            iconKey="printer"
            value={name}
            required
            placeholder="Ej. Impresora cocina"
            disabled={creating}
            onChange={(event) => setName(event.target.value)}
          />
          <InputField
            label="Nombre del dispositivo / cola"
            name="printerNewDevice"
            type="text"
            iconKey="code"
            value={printerName}
            required
            placeholder="Ej. POS-58 o EPSON_TM"
            disabled={creating}
            onChange={(event) => setPrinterName(event.target.value)}
          />
          <SelectField
            label="Tipo"
            name="printerNewType"
            value={printerType}
            iconKey="list"
            options={TYPE_OPTIONS}
            onChange={(event) =>
              setPrinterType(event.target.value as RestaurantPrinterType)
            }
          />
          <SelectField
            label="Conexión"
            name="printerNewConnection"
            value={connectionType}
            iconKey="store"
            options={CONNECTION_OPTIONS}
            onChange={(event) =>
              setConnectionType(
                event.target.value as RestaurantPrinterConnectionType
              )
            }
          />
          {isNetwork && (
            <>
              <InputField
                label="Dirección IP"
                name="printerNewIp"
                type="text"
                iconKey="store"
                value={ipAddress}
                required
                placeholder="Ej. 192.168.1.50"
                disabled={creating}
                onChange={(event) => setIpAddress(event.target.value)}
              />
              <InputField
                label="Puerto"
                name="printerNewPort"
                type="number"
                iconKey="code"
                value={port}
                min={1}
                disabled={creating}
                onChange={(event) =>
                  setPort(event.target.value ? Number(event.target.value) : '')
                }
              />
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <ActionButton
            label="Agregar"
            icon={<Check className="h-4 w-4" />}
            variant="primary"
            size="sm"
            fullWidth={false}
            loading={creating}
            disabled={!canSave}
            onClick={addPrinter}
          />
          <button
            type="button"
            className="text-xs text-bodydark2 hover:underline"
            onClick={() => {
              setShowNew(false);
              reset();
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <SelectField
        label="Impresora de comandas"
        name="printerId"
        value={value}
        iconKey="printer"
        disabled={disabled}
        placeholder="Seleccione una opción"
        options={[
          { value: 0, label: 'Sin impresora asignada' },
          ...printers.map((printer) => ({
            value: printer.id,
            label: printer.name,
          })),
        ]}
        onChange={(event) =>
          onChange(
            Number(event.target.value) === 0 ? '' : Number(event.target.value)
          )
        }
      />
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        onClick={() => setShowNew(true)}
      >
        <Plus className="h-3.5 w-3.5" />
        Nueva impresora
      </button>
    </div>
  );
};

export default PrinterQuickField;
